// Badge catalogue and checker (docs/achievements-design.md).
//
// Every badge is a row in BADGES: id, kind, name, icon, threshold and a
// rule over game state. Subject badges come in three levels — Bronze,
// Silver, Gold at 10 / 25 / 50 correct first-try answers — and are keyed
// by subject, so they carry across grades. A new subject needs only a
// SUBJECT_BADGES entry here (plus a RIDDLES entry — see
// data/riddles/index.js — and, to be offered to players, a
// data/courses.js course entry). `food` is kept though it's no longer a
// course: an older save may still carry progress against it.
//
// state.badges is a flat map from badge id to the local day it was
// earned: { "math:bronze": "2026-09-20" }. A key being present means
// earned; a null date means it was earned before dates were recorded.

export const BADGE_TIERS = [
  { id: "bronze", label: "Bronze", icon: "🥉", threshold: 10 },
  { id: "silver", label: "Silver", icon: "🥈", threshold: 25 },
  { id: "gold", label: "Gold", icon: "🥇", threshold: 50 }
];

export const SUBJECT_BADGES = {
  math: { label: "Maths Whiz" },
  english: { label: "Word Wizard" },
  science: { label: "Lab Legend" },
  history: { label: "Time Traveller" },
  geography: { label: "Geo Explorer" },
  computing: { label: "Code Breaker" },
  animals: { label: "Animal Expert" },
  plants: { label: "Plant Whiz" },
  "everyday-materials": { label: "Materials Master" },
  food: { label: "Food Scholar" }
};

export const subjectBadgeId = (subject, tierId) => `${subject}:${tierId}`;

export const BADGES = Object.entries(SUBJECT_BADGES).flatMap(([subject, { label }]) =>
  BADGE_TIERS.map((tier) => ({
    id: subjectBadgeId(subject, tier.id),
    kind: "subject",
    subject,
    tier: tier.id,
    label,
    name: `${tier.label} ${label}`,
    icon: tier.icon,
    threshold: tier.threshold,
    isMet: (state) => (state.subjectSolveCounts?.[subject] ?? 0) >= tier.threshold
  }))
);

const BADGES_BY_ID = new Map(BADGES.map((b) => [b.id, b]));
export const badgeById = (id) => BADGES_BY_ID.get(id) ?? null;

/** The device's local calendar day as YYYY-MM-DD — what an earned badge
 * records. A day, not a time: enough for "new this week", nothing more. */
export function localDateStamp(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Any stored badges value → the current flat map. Handles the old
 * per-subject shape ({ math: true }) from puzzle slice v2: each `true`
 * becomes that subject's Bronze, and any higher level the counts already
 * reached is filled in too — all with a null date, since they were earned
 * before dates existed (no invented date, no celebration). Shared by
 * persistence.js's migration and the teacher dashboard, which reads cloud
 * saves that may not have been upgraded yet.
 * @param {unknown} raw
 * @param {Record<string, number>} [subjectSolveCounts]
 * @returns {Record<string, string|null>}
 */
export function normalizeBadges(raw, subjectSolveCounts = {}) {
  const out = {};
  if (!raw || typeof raw !== "object") return out;
  let legacy = false;
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "boolean") {
      legacy = true;
      if (value && SUBJECT_BADGES[key]) out[subjectBadgeId(key, "bronze")] = null;
    } else if (key.includes(":") && (value === null || (typeof value === "string" && DATE_RE.test(value)))) {
      out[key] = value;
    }
  }
  if (legacy) {
    for (const badge of BADGES) {
      if (!(badge.id in out) && badge.isMet({ subjectSolveCounts })) out[badge.id] = null;
    }
  }
  return out;
}

/** Marks every newly met badge as earned `today`, in state.badges.
 * Returns the badges earned by *this call* (usually none or one) so a
 * caller can react — e.g. ui/puzzlePanel.js logs each to the activity
 * feed (systems/eventLog.js). */
export function checkBadges(state, today = localDateStamp()) {
  const newlyEarned = [];
  for (const badge of BADGES) {
    if (!(badge.id in state.badges) && badge.isMet(state)) {
      state.badges[badge.id] = today;
      newlyEarned.push(badge);
    }
  }
  return newlyEarned;
}

/**
 * One subject's badge standing: the highest level earned (and when), the
 * next level and progress toward it, and every level's state.
 * @returns {{
 *   subject: string, label: string, solved: number,
 *   current: { id: string, label: string, icon: string, earnedAt: string|null } | null,
 *   next: { id: string, label: string, icon: string, threshold: number } | null,
 *   progress: number, threshold: number, pct: number,
 *   tiers: Array<{ id: string, label: string, icon: string, threshold: number, earned: boolean, earnedAt: string|null }>
 * }}
 */
export function subjectBadgeStatus(badges, subject, solved = 0) {
  const tiers = BADGE_TIERS.map((t) => {
    const id = subjectBadgeId(subject, t.id);
    const earned = Boolean(badges) && id in badges;
    return { id: t.id, label: t.label, icon: t.icon, threshold: t.threshold, earned, earnedAt: earned ? badges[id] : null };
  });
  const earned = tiers.filter((t) => t.earned);
  const top = earned.at(-1) ?? null;
  const next = tiers.find((t) => !t.earned) ?? null;
  const threshold = (next ?? tiers.at(-1)).threshold;
  const progress = Math.min(solved, threshold);
  return {
    subject,
    label: SUBJECT_BADGES[subject]?.label ?? subject,
    solved,
    current: top && { id: top.id, label: top.label, icon: top.icon, earnedAt: top.earnedAt },
    next: next && { id: next.id, label: next.label, icon: next.icon, threshold: next.threshold },
    progress,
    threshold,
    pct: next ? Math.min(100, Math.round((solved / threshold) * 100)) : 100,
    tiers
  };
}

/** How many of `badges` were earned in the `days` days up to and
 * including `today` (both YYYY-MM-DD). Undated badges never count. */
export function badgesEarnedSince(badges, today, days = 7) {
  const [y, m, d] = today.split("-").map(Number);
  const from = localDateStamp(new Date(y, m - 1, d - (days - 1)));
  return Object.values(badges ?? {}).filter((at) => typeof at === "string" && at >= from && at <= today).length;
}
